'use client';

import { useState } from 'react';

interface NewsletterProps {
  variant?: 'default' | 'compact';
  className?: string;
}

export default function Newsletter({ variant = 'default', className = '' }: NewsletterProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setEmail('');
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }, 1500);
  };

  if (variant === 'compact') {
    return (
      <div className={`bg-gray-50 p-6 rounded-xl ${className}`}>
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-3">
            Stay Updated
          </h3>
          <p className="text-gray-600 mb-4 text-sm">
            Get notified about new articles and tips
          </p>
          <form onSubmit={handleSubmit} className="flex max-w-sm mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
            <button 
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary-600 text-white font-medium rounded-r-lg hover:bg-primary-700 transition-colors duration-200 disabled:opacity-50 text-sm"
            >
              {isSubmitting ? '...' : 'Subscribe'}
            </button>
          </form>
          {submitStatus === 'success' && (
            <p className="text-green-600 text-sm mt-2">Successfully subscribed!</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <section className={`py-16 bg-gray-50 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Stay Updated with Latest Articles
          </h3>
          <p className="text-gray-600 mb-6">
            Get notified when we publish new articles, tips, and success stories
          </p>
          
          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              Thank you for subscribing! You&apos;ll receive updates about our latest articles.
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button 
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 bg-primary-600 text-white font-medium rounded-r-lg hover:bg-primary-700 transition-colors duration-200 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                  Subscribing...
                </>
              ) : (
                'Subscribe'
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
